import clsx from 'clsx'
import React from 'react'

type Size = 'xs' | 's' | 'm'
type CellPosition = 'first' | 'middle' | 'last'

const bodyCellTypography: { [key in Size]: string } = {
  xs: 'text-xs text-gray-500',
  s: 'text-sm text-gray-500',
  m: 'text-sm text-gray-500',
}

const bodyCellPadding: { [key in Size]: { default: string; isDense: string } } = {
  xs: { default: 'py-1', isDense: 'py-0.5' },
  s: { default: 'py-2', isDense: 'py-1.5' },
  m: { default: 'py-4', isDense: 'py-2' },
}

const headerCellHorizontalPadding: {
  [key in CellPosition]: { default: string; isDense: string }
} = {
  first: {
    default: 'pl-4 pr-3 text-left lg:pl-8',
    isDense: 'pl-3 pr-2 text-left lg:pl-4',
  },
  middle: {
    default: 'px-3 text-left',
    isDense: 'px-2 text-left',
  },
  last: {
    default: 'pl-3 pr-4 text-right lg:pr-8',
    isDense: 'pl-2 pr-3 text-right lg:pr-4',
  },
}

const bodyCellHorizontalPadding: {
  [key in CellPosition]: { default: string; isDense: string }
} = {
  first: {
    default: 'pl-4 pr-3 sm:pl-4 md:pl-6 lg:pl-8',
    isDense: 'pl-3 pr-2 sm:pl-3 md:pl-4 lg:pl-4',
  },
  middle: {
    default: 'px-3',
    isDense: 'px-2',
  },
  last: {
    default: 'pl-3 pr-4 text-right sm:pr-4 md:pr-6 lg:pr-8',
    isDense: 'pl-2 pr-3 text-right sm:pr-3 md:pr-4 lg:pr-4',
  },
}

const getCellPosition = (index: number, lastIndex: number): CellPosition => {
  if (index === 0) return 'first'
  if (index === lastIndex) return 'last'
  return 'middle'
}

interface Props {
  head: string[]
  body: Array<(string | number | React.ReactNode)[]>

  size?: Size
  isDense?: boolean // Tighter cell padding and header height (for long lists).
  className?: string
  label?: string
  description?: React.ReactNode
  action?: React.ReactNode | null
}

const Table = (props: Props) => {
  const {
    head,
    body,

    size = 'm',
    isDense = false,
    className,
    label,
    description,
    action,
  } = props

  const bodyPaddingClass = isDense ? bodyCellPadding[size].isDense : bodyCellPadding[size].default

  return (
    <div className={className}>
      {(label || description || action) && (
        <div className='mx-8 my-4 flex items-center'>
          {(label || description) && (
            <div className='flex-auto'>
              {label && <h3 className='text-xl font-semibold text-gray-900'>{label}</h3>}

              {description && <p className='mt-2 text-sm text-gray-700'>{description}</p>}
            </div>
          )}

          {action && <div className='flex-none'>{action}</div>}
        </div>
      )}

      <div className='flex flex-col'>
        <div
          className={clsx(
            'inline-block min-w-full align-middle',
            isDense ? 'px-3 py-1.5' : 'px-4 py-2',
          )}
        >
          <div className='rounded-md shadow ring-1 ring-black/5'>
            <table className='min-w-full border-separate' style={{ borderSpacing: 0 }}>
              <thead className='bg-gray-50'>
                <tr>
                  {head.map((name, i) => {
                    const position = getCellPosition(i, head.length - 1)

                    return (
                      <th
                        key={name}
                        scope='col'
                        className={clsx(
                          isDense
                            ? headerCellHorizontalPadding[position].isDense
                            : headerCellHorizontalPadding[position].default,
                          'sticky top-0 z-10 whitespace-nowrap border-b border-gray-300 bg-gray-50/75 text-sm font-medium uppercase tracking-wide text-gray-500 backdrop-blur',
                          isDense ? 'py-2' : 'py-3.5',
                        )}
                      >
                        {name}
                      </th>
                    )
                  })}
                </tr>
              </thead>

              <tbody className='bg-white'>
                {body.map((row, i) => {
                  const isEvenRow = i % 2 === 1
                  const isLastRow = i !== body.length - 1

                  return (
                    <tr key={`${row[0]}-${i}`} className={clsx(isEvenRow && 'bg-gray-50')}>
                      {row?.map((cell, j) => {
                        const position = getCellPosition(j, row.length - 1)

                        return (
                          <td
                            key={`${cell}-${j}`}
                            className={clsx(
                              isLastRow && 'border-b border-gray-200',
                              isDense
                                ? bodyCellHorizontalPadding[position].isDense
                                : bodyCellHorizontalPadding[position].default,
                              bodyPaddingClass,
                              bodyCellTypography[size],
                              'whitespace-nowrap',
                            )}
                          >
                            {cell}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Table

